import { useEffect, useState } from "react";
import { Building2, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useOrganization } from "@/contexts/OrganizationContext";
import { createIntercompanyTag, updateIntercompanyTag, deleteIntercompanyTag } from "@/services/intercompanyTag.service";
import { formatAmount } from "@/utils/expenseFormatters";
import posthog from "@/lib/posthog";
import type { IntercompanyTagDto } from "@blinkbooks/types";

interface IntercompanyModalProps {
  open: boolean;
  onClose: () => void;
  entityId: string;
  /** The organization this entry itself lives in (not necessarily the one it should be kept under). */
  entityOrganizationId: string;
  vendor: string;
  amount: number;
  currency?: string;
  date: string;
  /** The current tag for this entry, or null if it hasn't been identified as intercompany yet. */
  existingTag: IntercompanyTagDto | null;
  onSaved: (tag: IntercompanyTagDto | null) => void;
}

const IntercompanyModal = ({
  open,
  onClose,
  entityId,
  entityOrganizationId,
  vendor,
  amount,
  currency,
  date,
  existingTag,
  onSaved,
}: IntercompanyModalProps) => {
  const { organizations } = useOrganization();
  const { toast } = useToast();
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Reopening the modal must reflect whatever was saved last time.
  useEffect(() => {
    if (!open) return;
    setSelectedOrgId(existingTag?.status === "assigned" ? existingTag.assignedOrganizationId : null);
  }, [open, existingTag]);

  const currentStatusLabel = existingTag
    ? existingTag.status === "assigned"
      ? `Currently assigned to ${organizations.find((o) => o.id === existingTag.assignedOrganizationId)?.name ?? "another company"}`
      : "Currently ignored — excluded from consolidated totals"
    : null;

  const persist = async (status: "assigned" | "ignored") => {
    setSaving(true);
    try {
      const assignedOrganizationId = status === "assigned" ? selectedOrgId ?? undefined : undefined;
      const tag = existingTag
        ? await updateIntercompanyTag(existingTag.id, { status, assignedOrganizationId })
        : await createIntercompanyTag({ entityType: "bank_transaction", entityId, status, assignedOrganizationId });
      posthog.capture(existingTag ? "intercompany_tag_updated" : "intercompany_tag_created", {
        entity_type: "bank_transaction",
        status,
      });
      onSaved(tag);
      onClose();
    } catch (error) {
      const message =
        (error as { status?: number })?.status === 403
          ? "You don't have access to that company. Switch to a view that includes it and try again."
          : "Failed to save. Please try again.";
      toast({ variant: "destructive", description: message });
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveTag = async () => {
    if (!existingTag) return;
    setSaving(true);
    try {
      await deleteIntercompanyTag(existingTag.id);
      posthog.capture("intercompany_tag_removed", { entity_type: "bank_transaction" });
      onSaved(null);
      onClose();
    } catch {
      toast({ variant: "destructive", description: "Failed to remove tag. Please try again." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            Intercompany entry
          </DialogTitle>
          <DialogDescription>
            This entry may also exist in another company's books. Choose which company should keep it in
            consolidated reporting, or ignore it entirely if it's a duplicate you don't need to track.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="rounded-md border border-border/60 bg-muted/30 px-3 py-2.5 text-[13px] space-y-1">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Vendor</span>
              <span className="font-medium text-foreground">{vendor || "—"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Amount</span>
              <span className="font-medium text-foreground tabular-nums">{formatAmount(amount, currency)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Date</span>
              <span className="font-medium text-foreground">{new Date(date).toLocaleDateString()}</span>
            </div>
          </div>

          {currentStatusLabel && (
            <p className="text-[12px] text-muted-foreground">{currentStatusLabel}</p>
          )}

          <div>
            <p className="text-[12px] font-medium text-foreground mb-2">Keep this entry under</p>
            <RadioGroup
              value={selectedOrgId ?? ""}
              onValueChange={setSelectedOrgId}
              className="max-h-48 overflow-y-auto pr-1"
            >
              {organizations.length === 0 ? (
                <p className="text-[12px] text-muted-foreground">No companies available.</p>
              ) : (
                organizations.map((org) => (
                  <div key={org.id} className="flex items-center gap-2">
                    <RadioGroupItem value={org.id} id={`ic-org-${org.id}`} />
                    <Label htmlFor={`ic-org-${org.id}`} className="text-[13px] font-normal cursor-pointer">
                      {org.name}
                      {org.id === entityOrganizationId && (
                        <span className="text-muted-foreground"> (this company)</span>
                      )}
                    </Label>
                  </div>
                ))
              )}
            </RadioGroup>
          </div>
        </div>

        <DialogFooter className="flex-col-reverse sm:flex-row sm:justify-between gap-2">
          {existingTag ? (
            <Button variant="ghost" size="sm" onClick={handleRemoveTag} disabled={saving} className="text-muted-foreground">
              Remove tag
            </Button>
          ) : <span />}
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => persist("ignored")} disabled={saving}>
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Ignore entry"}
            </Button>
            <Button onClick={() => persist("assigned")} disabled={saving || !selectedOrgId}>
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default IntercompanyModal;
